import { FATHOM_EVENTS, LEGACY_FATHOM_EVENTS, type AllFathomEventNames } from '@/lib/fathom/fathom-events';
import {
  AI_NATIVE_SDLC_ANNOUNCEMENT_BAR,
  AI_NATIVE_SDLC_PATH,
  AI_NATIVE_SDLC_STATUS,
  AI_NATIVE_SDLC_TITLE,
  type WhitepaperStatus,
} from '@/sections/resources/ebooks-and-whitepapers/ai-native-sdlc/publish-status';

/** What the site-wide announcement bar shows. Check with growth marketing before editing. */
export type Announcement = {
  message: string;
  linkText: string;
  /** Internal route (next/link). */
  href: string;
  /** `data-promo-offer` for GTM. */
  promoOffer: string;
  /** Campaign-versioned so a new announcement isn't suppressed by an earlier dismissal. */
  dismissCookie: string;
  /** Days the bar stays hidden after the visitor closes it (×). */
  dismissDays: number;
  /**
   * Days the bar stays hidden after the visitor opens `href` (by any route, not just the bar),
   * or `null` if visiting it doesn't hide the bar on other pages. Overrides a shorter manual
   * dismissal; a later manual dismissal never shortens it. The bar is always hidden on `href`
   * itself either way.
   * Invariant: `null` or `>= dismissDays` — the visit cookie replaces a close's and a later close
   * keeps it (cookieOnVisit / cookieOnDismiss), which only holds if a visit hides it at least as
   * long (announcement.test.ts checks every campaign).
   */
  hideAfterVisitDays: number | null;
  fathomEvent: AllFathomEventNames;
};

// The rebrand announcement, exactly as before (copy, promo id, cookie and event unchanged).
export const REBRAND_ANNOUNCEMENT: Announcement = {
  message: 'Gradle Technologies is now Develocity —',
  linkText: 'read the announcement',
  href: '/blog/gradle-technologies-is-now-develocity/',
  promoOffer: 'develocity-rebrand-announcement',
  dismissCookie: 'announcement_banner_closed_rebrand',
  dismissDays: 7,
  hideAfterVisitDays: null,
  fathomEvent: LEGACY_FATHOM_EVENTS.MARKETING_BANNER_LINK_CLICK,
};

// The paper is read on the page, ungated.
export const AI_NATIVE_SDLC_ANNOUNCEMENT: Announcement = {
  message: `New whitepaper: ${AI_NATIVE_SDLC_TITLE} —`,
  linkText: 'read the whitepaper',
  href: AI_NATIVE_SDLC_PATH,
  promoOffer: 'ai-native-sdlc-whitepaper',
  dismissCookie: 'announcement_banner_closed_ai-native-sdlc',
  dismissDays: 7,
  // Read it once and the bar stays hidden up to 400 days (Chrome's cap), i.e. for the campaign in
  // Chrome/Firefox. Safari caps script-set cookies at 7 days, so Safari visitors see the bar again
  // after a week.
  hideAfterVisitDays: 400,
  fathomEvent: FATHOM_EVENTS.banner.ai_native_sdlc.click,
};

/** The whitepaper takes the bar only once it's published AND its bar flag is on (a separate
 * sign-off, see publish-status.ts); otherwise it's the rebrand. */
export function pickAnnouncement(paperStatus: WhitepaperStatus, paperBar: boolean): Announcement {
  return paperStatus === 'published' && paperBar ? AI_NATIVE_SDLC_ANNOUNCEMENT : REBRAND_ANNOUNCEMENT;
}

export const ACTIVE_ANNOUNCEMENT = pickAnnouncement(AI_NATIVE_SDLC_STATUS, AI_NATIVE_SDLC_ANNOUNCEMENT_BAR);

const withoutTrailingSlash = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path);

/** True on the page the announcement links to: the bar stays hidden there rather than link the
 * page to itself. Trailing-slash tolerant; a null pathname (not yet known) is never it. */
export function isAnnouncementTarget(pathname: string | null, announcement: Pick<Announcement, 'href'>): boolean {
  return pathname !== null && withoutTrailingSlash(pathname) === withoutTrailingSlash(announcement.href);
}

/** The dismiss cookie's value once a visit to `href` hid the bar (a manual close stores "true"). */
export const VISITED_COOKIE_VALUE = 'visited';

export type BannerCookieWrite = { value: string; maxAgeSeconds: number };

const DAY_SECONDS = 60 * 60 * 24;

/** The cookie to write when the visitor closes the bar, or null to keep the current one (a
 * visit's hide is at least as long, so a later close must not shorten it). */
export function cookieOnDismiss(
  announcement: Pick<Announcement, 'dismissDays'>,
  currentValue: string | undefined,
): BannerCookieWrite | null {
  if (currentValue === VISITED_COOKIE_VALUE) return null;
  return { value: 'true', maxAgeSeconds: announcement.dismissDays * DAY_SECONDS };
}

/** The cookie to write when the visitor opens the announced page, or null if visits don't hide
 * this campaign's bar or a visit already did. Replaces a manual close's shorter cookie. */
export function cookieOnVisit(
  announcement: Pick<Announcement, 'hideAfterVisitDays'>,
  currentValue: string | undefined,
): BannerCookieWrite | null {
  if (announcement.hideAfterVisitDays === null || currentValue === VISITED_COOKIE_VALUE) return null;
  return { value: VISITED_COOKIE_VALUE, maxAgeSeconds: announcement.hideAfterVisitDays * DAY_SECONDS };
}

/** On `pathname`: whether the bar may show (`isAnnouncementTarget` hides it on the announced page
 * regardless), and the dismiss cookie to write, if any. A dismiss cookie, or a visit that writes
 * one, hides it. */
export function bannerStateForPage(
  pathname: string | null,
  announcement: Pick<Announcement, 'href' | 'hideAfterVisitDays'>,
  cookieValue: string | undefined,
): { show: boolean; write: BannerCookieWrite | null } {
  const write = isAnnouncementTarget(pathname, announcement) ? cookieOnVisit(announcement, cookieValue) : null;
  return { show: !write && cookieValue === undefined, write };
}
